import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap,
  Repeat,
  Send,
  BarChart3,
  ArrowRight,
  Settings2,
} from "lucide-react";
import automationImg from "../Accets/automation.jpg";

import {
  RevealSection,
  SplitText,
  MagneticButton,
  staggerContainer,
  staggerItem,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

const features = [
  { icon: Zap, title: "Instant Triggers", desc: "Run actions instantly on events." },
  { icon: Repeat, title: "Workflow Automation", desc: "Create multi-step business flows." },
  { icon: Send, title: "Auto Notifications", desc: "Send SMS, email, or webhook alerts." },
  { icon: BarChart3, title: "Smart Monitoring", desc: "Track every automated process." },
];

function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${
        center ? "mx-auto justify-center" : ""
      }`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// ---------------------------------------------------------------------
// NEW hero background — "gear chain" motif. Three interlocking gear
// outlines rotate at different speeds (two clockwise, one counter),
// connected by faint flow lines, like an automated workflow engine
// turning. Distinct from every other page's hero.
// ---------------------------------------------------------------------
function GearField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <DotGrid light />
      <span className="gear-glow" />

      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <line x1="70" y1="24" x2="82" y2="42" stroke="rgba(255,255,255,0.14)" strokeWidth="0.3" strokeDasharray="1.5 2" />
        <line x1="82" y1="42" x2="76" y2="64" stroke="rgba(217,164,65,0.2)" strokeWidth="0.3" strokeDasharray="1.5 2" />
      </svg>

      <svg className="gear gear-1" viewBox="0 0 100 100">
        <GearShape stroke={`${theme.accent}`} opacity="0.4" />
      </svg>
      <svg className="gear gear-2" viewBox="0 0 100 100">
        <GearShape stroke={`${theme.gold}`} opacity="0.35" teeth={10} />
      </svg>
      <svg className="gear gear-3" viewBox="0 0 100 100">
        <GearShape stroke="#FFFFFF" opacity="0.16" teeth={14} />
      </svg>
    </div>
  );
}

function GearShape({ stroke, opacity, teeth = 12 }) {
  const outerR = 40;
  const innerR = 34;
  const toothLen = 7;
  const points = [];
  for (let i = 0; i < teeth; i++) {
    const a1 = (i / teeth) * Math.PI * 2;
    const a2 = a1 + Math.PI / teeth;
    points.push(`${50 + innerR * Math.cos(a1)},${50 + innerR * Math.sin(a1)}`);
    points.push(`${50 + outerR * Math.cos((a1 + a2) / 2)},${50 + outerR * Math.sin((a1 + a2) / 2)}`);
    points.push(`${50 + innerR * Math.cos(a2)},${50 + innerR * Math.sin(a2)}`);
  }
  return (
    <>
      <polygon points={points.join(" ")} fill="none" stroke={stroke} strokeWidth="1.4" opacity={opacity} />
      <circle cx="50" cy="50" r="14" fill="none" stroke={stroke} strokeWidth="1" opacity={opacity} />
    </>
  );
}

export default function AutomationTools() {
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 40);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
        .gear-glow {
          position: absolute; top: 46%; left: 66%; width: 32rem; height: 32rem;
          margin: -16rem 0 0 -16rem; border-radius: 9999px;
          background: radial-gradient(circle, ${theme.accent}28, transparent 68%);
          filter: blur(70px);
          animation: gearGlowBreathe 7s ease-in-out infinite;
        }
        @keyframes gearGlowBreathe {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.12); }
        }

        .gear {
          position: absolute; width: 12rem; height: 12rem;
        }
        .gear-1 { top: 8%; right: 4%; animation: gearSpin 26s linear infinite; }
        .gear-2 { top: 34%; right: 22%; width: 8rem; height: 8rem; animation: gearSpin 20s linear infinite reverse; }
        .gear-3 { top: 54%; right: -2%; width: 15rem; height: 15rem; animation: gearSpin 36s linear infinite; }
        @keyframes gearSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (prefers-reduced-motion: reduce) {
          .gear-glow, .gear-1, .gear-2, .gear-3 { animation: none !important; }
        }

        .card-glow-light {
          pointer-events: none; position: absolute; top: -3rem; right: -3rem; width: 9rem; height: 9rem;
          border-radius: 9999px; filter: blur(30px); background: ${theme.accent}; opacity: 0.08; transition: opacity 0.3s ease;
        }
        .group:hover .card-glow-light { opacity: 0.18; }
      `}</style>

      {/* ================= HERO (dark, gear field) ================= */}
      <section ref={heroRef} className="relative overflow-hidden pt-40 pb-24 md:pt-48 md:pb-32" style={{ backgroundColor: theme.heroBg }}>
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
          <GearField />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <Eyebrow light>Automation Tools</Eyebrow>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight text-white">
              <SplitText text="Automate your" />
              <br />
              <span style={{ color: theme.accent }}>
                <SplitText text="business workflows" delay={0.25} />
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="text-white/60 mt-8 text-lg leading-8 max-w-xl"
            >
              Build powerful workflows, automate payments, trigger notifications,
              and manage operations without manual effort.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="mt-10"
            >
              <MagneticButton>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group inline-flex items-center gap-2 px-8 py-4 rounded-md text-white font-semibold hover:brightness-110 transition-all"
                >
                  Get Started
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </MagneticButton>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
            className="relative"
          >
            <div className="absolute inset-0 rounded-[32px] blur-[100px] opacity-20" style={{ backgroundColor: theme.accent }} />
            <img src={automationImg} alt="Automation Tools" className="relative rounded-[32px] border border-white/10 shadow-2xl" />
          </motion.div>
        </div>
      </section>

      {/* ================= FEATURES (light) ================= */}
      <section className="relative py-24" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-7xl mx-auto px-6">
          <RevealSection className="text-center max-w-2xl mx-auto mb-16">
            <Eyebrow center>Capabilities</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-bold" style={{ color: theme.ink }}>
              Why choose <span style={{ color: theme.accent }}>Automation Tools?</span>
            </h2>
            <p className="mt-4" style={{ color: theme.inkMuted }}>
              Reduce manual work and scale operations efficiently.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((item) => (
              <motion.div
                key={item.title}
                variants={staggerItem}
                whileHover={{ y: -8 }}
                className="group relative rounded-3xl p-8 overflow-hidden"
                style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}
              >
                <span className="card-glow-light" />
                <div className="relative w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}>
                  <item.icon size={26} />
                </div>
                <h3 className="relative text-2xl font-semibold" style={{ color: theme.ink }}>{item.title}</h3>
                <p className="relative mt-4" style={{ color: theme.inkMuted }}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Let's Talk</Eyebrow>
          <h2 className="text-4xl md:text-5xl font-bold leading-tight text-white">
            <SplitText text="Automate" />
            <span className="block">
              <SplitText text="everything today." delay={0.15} />
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Save time, reduce errors, and scale your business faster.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Contact Sales
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}